/* ============================================
   DRIFT Theme by WEBEXP, LLC
   DRIFT Site Background — Gradient Initialization
   Loaded by drift-background.liquid when the site-wide animated gradient
   is enabled in theme settings. Initializes drift-bg-shader.js on the
   site-level canvas using the theme's light/dark seed colors. The DRIFT
   Product Feed section can override the palette via window.driftBgShader
   .setColors() when per-product metafields are set.
   https://webexp.dev
   ============================================ */
(function () {
  'use strict';

  var bg = document.querySelector('[data-drift-site-bg]');
  if (!bg) return;
  var canvas = bg.querySelector('[data-drift-site-bg-canvas]');
  if (!canvas) return;

  /* === HSL helpers (mirror the section-level derivation in drift-feed.js) === */
  function parseHex(hex) {
    if (!hex) return null;
    hex = hex.trim().replace(/^#/, '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    if (hex.length !== 6) return null;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  }
  function rgbToHsl(rgb) {
    var r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h *= 60;
    }
    return { h: h, s: s * 100, l: l * 100 };
  }
  function hslToHex(h, s, l) {
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs((h / 60) % 2 - 1));
    var m = l - c / 2;
    var r = 0, g = 0, b = 0;
    if (h < 60)       { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else              { r = c; g = 0; b = x; }
    var toHex = function (v) {
      var n = Math.round((v + m) * 255).toString(16);
      return n.length === 1 ? '0' + n : n;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }
  function derivePalette(seedHex) {
    var rgb = parseHex(seedHex);
    if (!rgb) return null;
    var hsl = rgbToHsl(rgb);
    var h = hsl.h, s = hsl.s, l = hsl.l;
    /* Tightened blend (v3.30.1). The four anchors used to span 60 points of
       lightness, which gave each one its own territory and read as hard
       colour blocks. It also meant a merchant who set one hex in the
       feed_bg_color metafield saw four visibly different colours, none of
       them quite what they picked. Half the spread keeps the seed
       recognisable and lets the anchors melt into each other. */
    if (s < 8) {
      return [
        hslToHex(0, 0, Math.min(96, Math.max(20, l + 20))),
        hslToHex(0, 0, l + 2),
        hslToHex(0, 0, Math.min(88, Math.max(8, l - 9))),
        hslToHex(0, 0, Math.min(75, Math.max(4, l - 20)))
      ];
    }
    s = Math.max(25, s);
    return [
      hslToHex(h, Math.max(15, s - 4), Math.min(94, l + 20)),
      hslToHex(h, s, l + 2),
      hslToHex((h + 26) % 360, Math.min(95, s + 6), Math.max(20, l - 7)),
      hslToHex(h, Math.min(95, s + 5), Math.max(8, l - 20))
    ];
  }

  /* Pick the seed for the current mode. */
  function currentSeed() {
    var mode = document.documentElement.getAttribute('data-drift-mode') || 'dark';
    return mode === 'light'
      ? bg.getAttribute('data-gradient-light')
      : bg.getAttribute('data-gradient-dark');
  }

  /* The "target palette" is the palette we WANT to display right now. It's
     updated by override/applyFromSeed/reset. It's also what bootShader uses
     to init — meaning if drift-feed.js or the PDP script call override
     BEFORE the shader script has loaded, the shader still inits with the
     correct palette (no flash to theme defaults then snap to metafield). */
  var targetPalette = null;

  function deriveThemeDefaults() {
    var seed = currentSeed();
    return seed ? derivePalette(seed) : null;
  }

  function applyThemeDefaults() {
    targetPalette = deriveThemeDefaults();
    if (targetPalette && window.driftBgShader) {
      window.driftBgShader.setColors(targetPalette);
    }
  }

  /* Expose a global API used by:
       - drift-feed.js: per-product overrides as the feed swipes
       - PDP / other pages: push the active product's metafield color
     Both update targetPalette so the shader picks it up whenever it loads. */
  window.driftSiteBg = {
    override: function (palette) {
      if (!palette) return;
      targetPalette = palette;
      if (window.driftBgShader) window.driftBgShader.setColors(palette);
    },
    applyFromSeed: function (seedHex) {
      var palette = derivePalette(seedHex);
      if (palette) this.override(palette);
    },
    reset: applyThemeDefaults
  };

  /* Signal that the API is available so any code that loaded earlier (e.g.
     drift-feed.js which initialized before this script finished) can push
     a deferred palette now. drift-feed.js listens for this event and re-
     applies the current slide's bg if it wasn't applied initially. */
  try {
    document.dispatchEvent(new CustomEvent('driftSiteBg:ready'));
  } catch (e) {}

  /* Lazy-load the shader, init with whatever target palette is current at
     the time the shader actually loads. This handles the race where
     drift-feed.js / PDP script call override() before the shader is ready
     — by the time init runs, targetPalette has the metafield-derived
     palette, so the very first paint already shows the right colors. */
  /* Resolve a per-product seed synchronously from the DOM so the shader's
     FIRST paint is already the right palette. Without this, warm-cache
     navigations boot the shader with theme defaults (cached shader script
     loads faster than the async palette pushes from the PDP/feed scripts),
     then visibly morph to the product color ~100ms later. Sources, in
     priority order:
       1. window.__driftSeedHint — set synchronously in theme.liquid <head>
          on PDPs whose product has a feed_bg_color metafield.
       2. The active feed slide's data-feed-bg-color (index from the
          ?feed_index= URL param when returning from a PDP, else slide 0).
     Skipped when the Enter Page overlay is up — it wants neutral theme
     colors until dismissed. */
  function domSeedHint() {
    var enterEl = document.querySelector('[data-enter-page]');
    if (enterEl) return null;
    if (window.__driftSeedHint) return window.__driftSeedHint;
    /* If the visitor's saved feed view is grid, the feed boots hidden —
       seed with the neutral theme palette, not a product metafield color. */
    try {
      if (localStorage.getItem('drift-feed-view') === 'grid') return null;
    } catch (e) {}
    var feed = document.querySelector('.drift-feed[data-bg-morph="true"][data-bg-style="gradient"]');
    if (feed) {
      var slides = feed.querySelectorAll('.drift-feed__slide');
      if (slides.length) {
        var idx = 0;
        try {
          var p = new URLSearchParams(window.location.search).get('feed_index');
          if (p !== null) {
            var n = parseInt(p, 10);
            if (!isNaN(n) && n >= 0 && n < slides.length) idx = n;
          }
        } catch (e) {}
        var slide = slides[idx];
        if (slide && !slide.getAttribute('data-feed-bg-media-url')) {
          var c = slide.getAttribute('data-feed-bg-color');
          if (c) return c;
        }
      }
    }
    return null;
  }

  /* Palette continuity across navigations: the previous page stores its
     final palette in sessionStorage on pagehide. On boot, if it differs
     from this page's target, init the shader with the PREVIOUS palette and
     then morph (600ms shader lerp) to the new one — so the background
     visibly color-morphs through the navigation instead of snapping. */
  var PALETTE_KEY = 'drift-bg-palette';
  var prevPalette = null;
  try { prevPalette = JSON.parse(sessionStorage.getItem(PALETTE_KEY) || 'null'); } catch (e) {}

  window.addEventListener('pagehide', function () {
    try { if (targetPalette) sessionStorage.setItem(PALETTE_KEY, JSON.stringify(targetPalette)); } catch (e) {}
  });

  function bootShader() {
    if (!targetPalette) {
      var hint = domSeedHint();
      if (hint) targetPalette = derivePalette(hint);
    }
    if (!targetPalette) targetPalette = deriveThemeDefaults() || ['#222', '#444', '#666', '#888'];

    var bootPalette = targetPalette;
    var needsMorph = false;
    if (prevPalette && prevPalette.length === 4 && JSON.stringify(prevPalette) !== JSON.stringify(targetPalette)) {
      bootPalette = prevPalette;
      needsMorph = true;
    }

    function afterInit() {
      if (needsMorph && window.driftBgShader) {
        requestAnimationFrame(function () {
          window.driftBgShader.setColors(targetPalette);
        });
      }
    }

    if (window.driftBgShader && window.driftBgShader.init(canvas, bootPalette)) { afterInit(); return; }
    var src = bg.getAttribute('data-bg-shader-src');
    if (!src) return;
    var s = document.createElement('script');
    s.src = src;
    s.onload = function () {
      if (window.driftBgShader) {
        /* Boot with the previous page's palette (if it differed) so the
           morph to the current target plays out visibly. Override calls
           between then and now update targetPalette and are reflected. */
        window.driftBgShader.init(canvas, needsMorph ? bootPalette : targetPalette);
        afterInit();
      }
    };
    document.head.appendChild(s);
  }
  bootShader();

  /* Re-derive the palette when the user toggles light/dark mode. */
  var modeObs = new MutationObserver(function () { applyThemeDefaults(); });
  modeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-drift-mode'] });
})();
